import Add from "@mui/icons-material/Add";
import AttachFile from "@mui/icons-material/AttachFile";
import Check from "@mui/icons-material/Check";
import CheckCircle from "@mui/icons-material/CheckCircle";
import ErrorOutline from "@mui/icons-material/ErrorOutline";
import MoreVert from "@mui/icons-material/MoreVert";
import PlayArrow from "@mui/icons-material/PlayArrow";
import RadioButtonChecked from "@mui/icons-material/RadioButtonChecked";
import RadioButtonUnchecked from "@mui/icons-material/RadioButtonUnchecked";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import Replay from "@mui/icons-material/Replay";
import {
  Box,
  Button,
  Chip,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
// Shared with the API's advanceCustomWorkflow and the registrar's reconcile, so
// "finished" means the same thing in all three. Strings rather than an enum
// because the GraphQL ActivityStatus here and Prisma's are different nominal
// types with identical members.
import { isTerminalStepStatus } from "encamp-shared/src/activity/stepStatuses";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { ActivityStatus, CustomWorkDetailFieldsFragment } from "generated-graphql/graphql";
import { DateTime } from "luxon";
import { useState } from "react";

export type WorkStep = CustomWorkDetailFieldsFragment["steps"][number];

type WorkStepsListProps = {
  steps: WorkStep[];
  /**
   * False once the workflow is terminal — open-step actions and attachment go
   * read-only. Undo stays available regardless, so a finished deliverable can
   * still be walked back.
   */
  canEdit: boolean;
  busy?: boolean;
  onStart: (activityId: string) => void;
  onComplete: (activityId: string) => void;
  onSkip: (activityId: string) => void;
  onReopen: (activityId: string) => void;
  onAssign: (activityId: string) => void;
  onAttachDocument: (step: WorkStep) => void;
  onOpenDocument: (documentId: string) => void;
  /** Resolves true once the step is on the deliverable, so the field can clear. */
  onAddStep: (description: string) => Promise<boolean>;
};

function StepStatusIcon({ step }: { step: WorkStep }) {
  if (step.status === ActivityStatus.Completed || step.status === ActivityStatus.StaffCompleted) {
    return <CheckCircle color="success" />;
  }
  if (step.status === ActivityStatus.Skipped) {
    return <RemoveCircleOutline color="disabled" />;
  }
  if (step.status === ActivityStatus.Error) {
    return <ErrorOutline color="error" />;
  }
  if (step.isCurrent || step.status === ActivityStatus.InProgress) {
    return <RadioButtonChecked color="warning" />;
  }
  return <RadioButtonUnchecked color="action" />;
}

/** "Completed by ben@… · Jul 23, 2:14 PM" — or nothing if never touched. */
function statusLine(step: WorkStep): string | null {
  if (!step.statusChangedAt) {
    return null;
  }
  const when = DateTime.fromISO(step.statusChangedAt).toLocaleString(DateTime.DATETIME_MED);
  const who = step.statusChangedByEmail ? ` by ${step.statusChangedByEmail}` : "";
  if (isTerminalStepStatus(step.status)) {
    return `${prettyPrintEnumValue(step.status)}${who} · ${when}`;
  }
  if (step.status === ActivityStatus.InProgress) {
    return `Started${who} · ${when}`;
  }
  // Back at NotStarted with a log entry behind it: somebody walked it back.
  return `Reopened${who} · ${when}`;
}

type StepActionsProps = {
  step: WorkStep;
  terminal: boolean;
  canEdit: boolean;
  busy?: boolean;
  onStart: (activityId: string) => void;
  onComplete: (activityId: string) => void;
  onSkip: (activityId: string) => void;
  onReopen: (activityId: string) => void;
  onAssign: (activityId: string) => void;
};

/** Walks a finished step back out of its terminal state. */
function UndoAction({
  step,
  busy,
  onReopen,
}: Pick<StepActionsProps, "step" | "busy" | "onReopen">) {
  return (
    <Tooltip title="Undo — back to Not started. The original action stays in the history.">
      <span>
        <Button
          size="small"
          startIcon={<Replay />}
          disabled={busy}
          onClick={() => onReopen(step.id)}
        >
          Undo
        </Button>
      </span>
    </Tooltip>
  );
}

/**
 * The step's trailing controls. An open step gets Start (until it is under
 * way), Complete, and an overflow menu; a finished one gets Undo. Undo is
 * exempt from `canEdit` — a one-step deliverable would otherwise be
 * unrecoverable the moment it completed. Owns its own menu anchor so the card
 * stays layout.
 */
function StepActions({
  step,
  terminal,
  canEdit,
  busy,
  onStart,
  onComplete,
  onSkip,
  onReopen,
  onAssign,
}: StepActionsProps) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  if (terminal) {
    return <UndoAction step={step} busy={busy} onReopen={onReopen} />;
  }
  if (!canEdit) {
    return null;
  }

  const started = step.status === ActivityStatus.InProgress;

  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      {!started && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<PlayArrow />}
          disabled={busy}
          onClick={() => onStart(step.id)}
        >
          Start
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<Check />}
        disabled={busy}
        onClick={() => onComplete(step.id)}
      >
        Complete
      </Button>
      <IconButton
        size="small"
        aria-label={`step actions for ${step.description}`}
        disabled={busy}
        onClick={(event) => setMenuAnchor(event.currentTarget)}
      >
        <MoreVert fontSize="small" />
      </IconButton>
      <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
        {started && (
          <MenuItem
            onClick={() => {
              setMenuAnchor(null);
              onReopen(step.id);
            }}
          >
            <ListItemText>Back to Not started</ListItemText>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onSkip(step.id);
          }}
        >
          <ListItemText>Skip</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            onAssign(step.id);
          }}
        >
          <ListItemText>Assign…</ListItemText>
        </MenuItem>
      </Menu>
    </Stack>
  );
}

function StepCard({
  step,
  canEdit,
  busy,
  onStart,
  onComplete,
  onSkip,
  onReopen,
  onAssign,
  onAttachDocument,
  onOpenDocument,
}: { step: WorkStep } & Omit<WorkStepsListProps, "steps">) {
  const terminal = isTerminalStepStatus(step.status);
  const skipped = step.status === ActivityStatus.Skipped;
  const line = statusLine(step);

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        px: 2,
        py: 1.5,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        borderRadius: 2,
        ...(step.isCurrent && {
          borderColor: theme.palette.warning.main,
          boxShadow: `0 0 0 1px ${theme.palette.warning.main}`,
        }),
        ...(skipped && { borderStyle: "dashed", opacity: 0.7 }),
      })}
    >
      <Box sx={{ pt: 0.25 }}>
        <StepStatusIcon step={step} />
      </Box>

      <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.25}>
        <Typography
          variant="body1"
          sx={{
            fontWeight: step.isCurrent ? 600 : 500,
            textDecoration: skipped ? "line-through" : "none",
          }}
        >
          {step.description}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {step.assigneeEmail ?? "Unassigned"}
          {line ? ` · ${line}` : ""}
        </Typography>
        {(step.documents.length > 0 || (canEdit && !terminal)) && (
          <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ pt: 0.75 }}>
            {step.documents.map((doc) => (
              <Chip
                key={doc.documentId}
                size="small"
                variant="outlined"
                icon={<AttachFile />}
                label={doc.title}
                onClick={() => onOpenDocument(doc.documentId)}
              />
            ))}
            {canEdit && !terminal && (
              <Button
                size="small"
                startIcon={<AttachFile />}
                onClick={() => onAttachDocument(step)}
                sx={{ alignSelf: "center" }}
              >
                Attach
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      <StepActions
        step={step}
        terminal={terminal}
        canEdit={canEdit}
        busy={busy}
        onStart={onStart}
        onComplete={onComplete}
        onSkip={onSkip}
        onReopen={onReopen}
        onAssign={onAssign}
      />
    </Paper>
  );
}

/**
 * The quiet way to grow a deliverable: a description is all a hand-run step
 * needs. Enter submits so the common case never reaches for the button, and the
 * field only clears once the server has the step.
 */
function AddStepRow({
  busy,
  onAdd,
}: {
  busy?: boolean;
  onAdd: (description: string) => Promise<boolean>;
}) {
  const [description, setDescription] = useState("");
  const trimmed = description.trim();

  const submit = async () => {
    if (trimmed.length === 0) {
      return;
    }
    const added = await onAdd(trimmed);
    if (added) {
      setDescription("");
    }
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField
        size="small"
        fullWidth
        label="Add a step"
        placeholder="Add a step…"
        value={description}
        disabled={busy}
        onChange={(event) => setDescription(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            void submit();
          }
        }}
      />
      <Button
        size="small"
        startIcon={<Add />}
        disabled={busy || trimmed.length === 0}
        onClick={() => void submit()}
      >
        Add
      </Button>
    </Stack>
  );
}

/**
 * The deliverable's steps as activity cards, each carrying its own attachments
 * and its last who/when status line. A custom deliverable's steps are strictly
 * sequential and the API hands them over in that order, so the list renders
 * them as received and only numbers them so the sequence stays legible. Staff
 * can append another step to the bottom of the list while the deliverable is
 * still open.
 */
export function WorkStepsList(props: WorkStepsListProps) {
  const { steps, canEdit, busy, onAddStep } = props;

  return (
    <Stack spacing={1.5}>
      {steps.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No steps yet.
        </Typography>
      )}
      {steps.map((step, index) => (
        <Box key={step.id}>
          <Typography variant="overline" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            Step {index + 1}
          </Typography>
          <StepCard step={step} {...props} />
        </Box>
      ))}
      {canEdit && <AddStepRow busy={busy} onAdd={onAddStep} />}
    </Stack>
  );
}
